import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CalendarDays,
  ChevronRight,
  Download,
  Plus,
  User,
  X,
} from 'lucide-react';
import { LoadingIndicator, LoadingSpinner } from './LoadingIndicator';
import { Dialog } from './Dialog';
import { Button } from './Button';
import {
  listProjectRuns,
  ProjectRunRow,
  ProjectRunStatus,
} from '../../services/projectRunsApi';
import { CreateRunModal } from '../../pages/dashboard/common/CreateRunModal';
import { InfrastructureStatusNotice } from './InfrastructureStatusNotice';
import {
  formatOrgProjectScopeLabel,
  isProjectInfrastructureReady,
  normalizeProjectProvisionStatus,
  isProvisioningActive,
  ProjectProvisionStatus,
} from '../../types/onboarding';
import { twMerge as cn } from 'tailwind-merge';

const brandOutlineButtonClass =
  'h-9 gap-1.5 border-2 border-brand bg-white text-brand hover:bg-brand/5';

function formatRunDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(d);
  } catch {
    return iso;
  }
}

function statusBadgeClasses(status: ProjectRunStatus): string {
  switch (status) {
    case 'COMPLETED':
      return 'bg-green-100 text-green-800';
    case 'FAILED':
      return 'bg-red-100 text-red-800';
    case 'IN_PROGRESS':
    default:
      return 'bg-amber-100 text-amber-900';
  }
}

function statusLabel(status: ProjectRunStatus): string {
  switch (status) {
    case 'COMPLETED':
      return 'Completed';
    case 'FAILED':
      return 'Failed';
    case 'IN_PROGRESS':
    default:
      return 'In progress';
  }
}

export interface ProjectRunsModalProps {
  open: boolean;
  onClose: () => void;
  projectId: number | null;
  customerName: string;
  projectName: string;
  onViewRun: (runId: number) => void;
  onDownloadPerformanceReport: (runId: number) => Promise<void>;
  onDownloadQueryLogReport: (runId: number) => Promise<void>;
}

export function ProjectRunsModal({
  open,
  onClose,
  projectId,
  customerName,
  projectName,
  onViewRun,
  onDownloadPerformanceReport,
  onDownloadQueryLogReport,
}: ProjectRunsModalProps) {
  const titleId = 'project-runs-modal-title';
  const summaryId = 'project-runs-modal-summary';
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const [runs, setRuns] = useState<ProjectRunRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreRuns, setHasMoreRuns] = useState(false);
  const [totalRunCount, setTotalRunCount] = useState(0);
  const [inProgressTotal, setInProgressTotal] = useState<number | null>(null);
  const [runsPage, setRunsPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [newRunOpen, setNewRunOpen] = useState(false);
  const [provisionStatus, setProvisionStatus] = useState<
    ProjectProvisionStatus | undefined
  >();
  const [provisionError, setProvisionError] = useState<string | undefined>();
  const [runDownloadError, setRunDownloadError] = useState<string | null>(null);
  const [pendingDownload, setPendingDownload] = useState<{
    runId: number;
    kind: 'performance' | 'querylog';
  } | null>(null);

  const infrastructureReady = isProjectInfrastructureReady(provisionStatus);

  const loadRuns = useCallback(async (pid: number, page = 0, replace = true) => {
    const res = await listProjectRuns(pid, { pageNumber: page, pageSize: 25 });
    if (replace) {
      setProvisionStatus(normalizeProjectProvisionStatus(res.provision_status));
      setProvisionError(res.provision_error?.trim() || undefined);
    }
    const list = res.runs ?? [];
    setRuns((prev) => (replace ? list : [...prev, ...list]));
    setRunsPage(page);
    setHasMoreRuns(res.has_more ?? false);
    setTotalRunCount(res.total_count ?? list.length);
    if (typeof res.in_progress_count === 'number') {
      setInProgressTotal(res.in_progress_count);
    } else if (replace) {
      setInProgressTotal(null);
    }
    return res;
  }, []);

  useEffect(() => {
    if (!open || projectId == null) return;

    let cancelled = false;
    setLoading(true);
    setError(null);
    setInProgressTotal(null);
    setProvisionStatus(undefined);
    setProvisionError(undefined);
    setRunDownloadError(null);
    setPendingDownload(null);

    loadRuns(projectId, 0, true)
      .catch(() => {
        if (!cancelled) setError('Could not load runs. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, projectId, loadRuns]);

  useEffect(() => {
    if (!open || projectId == null || !isProvisioningActive(provisionStatus)) {
      return;
    }

    const intervalId = window.setInterval(() => {
      void loadRuns(projectId, 0, true).catch(() => {
        /* best-effort refresh while provisioning */
      });
    }, 20000);

    return () => window.clearInterval(intervalId);
  }, [open, projectId, provisionStatus, loadRuns]);

  const loadedInProgressCount = useMemo(
    () => runs.filter((r) => r.status === 'IN_PROGRESS').length,
    [runs]
  );
  const inProgressCount =
    inProgressTotal ??
    (hasMoreRuns ? null : loadedInProgressCount);

  const refreshRuns = async () => {
    if (projectId == null) return;
    try {
      await loadRuns(projectId, 0, true);
    } catch {
      /* best-effort */
    }
  };

  const loadMoreRuns = async () => {
    if (projectId == null || loadingMore || !hasMoreRuns) return;
    setLoadingMore(true);
    try {
      await loadRuns(projectId, runsPage + 1, false);
    } catch {
      /* best-effort */
    } finally {
      setLoadingMore(false);
    }
  };

  const openRun = (runId: number) => {
    onViewRun(runId);
    onClose();
  };

  const handleRunDownload = async (
    runId: number,
    kind: 'performance' | 'querylog'
  ) => {
    setRunDownloadError(null);
    setPendingDownload({ runId, kind });
    try {
      if (kind === 'performance') {
        await onDownloadPerformanceReport(runId);
      } else {
        await onDownloadQueryLogReport(runId);
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Could not download the report.';
      setRunDownloadError(message);
    } finally {
      setPendingDownload(null);
    }
  };

  const headerTitle = formatOrgProjectScopeLabel(customerName, projectName);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        labelledBy={titleId}
        describedBy={summaryId}
        initialFocusRef={closeBtnRef}
        panelClassName="max-h-[90vh] max-w-lg overflow-hidden p-0 shadow-2xl"
      >
        <div className="flex max-h-[90vh] flex-col">
          <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
            <div className="min-w-0 flex-1">
              <h2
                id={titleId}
                className="truncate text-lg font-bold text-brand"
                title={headerTitle.truncated ? headerTitle.full : undefined}
              >
                {headerTitle.display}
              </h2>
              {!loading && !error && (
                <p id={summaryId} className="mt-1 text-sm text-gray-600">
                  {totalRunCount || runs.length} runs
                  {inProgressCount != null && inProgressCount > 0
                    ? ` (${inProgressCount} in progress)`
                    : ''}
                </p>
              )}
            </div>
            <button
              ref={closeBtnRef}
              type="button"
              onClick={onClose}
              className="shrink-0 rounded-md p-2 text-gray-600 hover:bg-gray-100 hover:text-brand"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="min-h-[220px] flex-1 overflow-y-auto px-5 py-4">
            {!loading && !error && !infrastructureReady && (
              <InfrastructureStatusNotice
                status={provisionStatus}
                provisionError={provisionError}
                className="mb-4"
              />
            )}
            {loading && (
              <div className="flex justify-center py-16">
                <LoadingIndicator label="Loading runs..." size="lg" />
              </div>
            )}
            {!loading && error && (
              <p className="py-12 text-center text-sm text-red-600">{error}</p>
            )}
            {!loading && !error && runs.length === 0 && (
              <p className="py-12 text-center text-sm text-gray-600">
                No performance runs found for this project yet.
              </p>
            )}
            {!loading && !error && runs.length > 0 && (
              <>
                {runDownloadError && (
                  <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {runDownloadError}
                  </div>
                )}
                <ul className="max-h-[50vh] space-y-3 overflow-y-auto">
                  {runs.map((run) => {
                    const meta = run.metadata ?? {};
                    const stepHint =
                      (meta.step as string) ||
                      (meta.label as string) ||
                      (meta.phase as string) ||
                      null;
                    const failureReason = (meta.failure_reason as string)?.trim();
                    const createdBy = run.created_by_name;
                    const perfDownloading =
                      pendingDownload?.runId === run.run_id &&
                      pendingDownload.kind === 'performance';
                    const logDownloading =
                      pendingDownload?.runId === run.run_id &&
                      pendingDownload.kind === 'querylog';

                    return (
                      <li
                        key={run.run_id}
                        className="overflow-hidden rounded-lg border border-gray-200 bg-gray-50 shadow-sm"
                      >
                        <button
                          type="button"
                          className="flex w-full items-center justify-between bg-gray-50 px-4 py-3 text-left transition-colors hover:bg-gray-100"
                          onClick={() => openRun(run.run_id)}
                        >
                          <div className="min-w-0 flex-1 pr-3">
                            <div className="mb-1.5 flex flex-wrap items-center gap-2">
                              <p className="font-medium text-gray-900">
                                Run {run.run_number ?? run.run_id}
                              </p>
                              <span
                                className={cn(
                                  'inline-flex rounded px-2 py-0.5 text-xs font-medium',
                                  statusBadgeClasses(run.status)
                                )}
                              >
                                {statusLabel(run.status)}
                              </span>
                            </div>
                            <div className="space-y-1 text-sm text-gray-600">
                              {stepHint && run.status === 'IN_PROGRESS' ? (
                                <p className="text-sm text-gray-700">{stepHint}</p>
                              ) : null}
                              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                                <CalendarDays
                                  className="h-4 w-4 shrink-0 text-gray-400"
                                  aria-hidden
                                />
                                <span>{formatRunDate(run.created_at)}</span>
                                {createdBy ? (
                                  <>
                                    <span className="text-gray-300" aria-hidden>
                                      ·
                                    </span>
                                    <User
                                      className="h-4 w-4 shrink-0 text-gray-400"
                                      aria-hidden
                                    />
                                    <span>{createdBy}</span>
                                  </>
                                ) : null}
                              </div>
                              {run.status === 'FAILED' && failureReason ? (
                                <p className="text-xs text-red-600">
                                  {failureReason}
                                </p>
                              ) : null}
                            </div>
                          </div>
                          <ChevronRight
                            className="h-5 w-5 shrink-0 text-gray-400"
                            aria-hidden
                          />
                        </button>

                        {run.status === 'COMPLETED' && (
                          <div className="flex gap-2 px-4 pb-3">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={pendingDownload !== null}
                              className={cn(
                                'flex-1 text-xs leading-tight',
                                brandOutlineButtonClass
                              )}
                              title="Download the performance report PDF for this run"
                              aria-label="Download performance report PDF"
                              onClick={(event) => {
                                event.stopPropagation();
                                void handleRunDownload(run.run_id, 'performance');
                              }}
                            >
                              {perfDownloading ? (
                                <LoadingSpinner size="xs" />
                              ) : (
                                <Download className="h-3.5 w-3.5" aria-hidden />
                              )}
                              Performance report
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={pendingDownload !== null}
                              className={cn(
                                'flex-1 text-xs leading-tight',
                                brandOutlineButtonClass
                              )}
                              title="Download the query log analysis PDF for this run"
                              aria-label="Download query log analysis PDF"
                              onClick={(event) => {
                                event.stopPropagation();
                                void handleRunDownload(run.run_id, 'querylog');
                              }}
                            >
                              {logDownloading ? (
                                <LoadingSpinner size="xs" />
                              ) : (
                                <Download className="h-3.5 w-3.5" aria-hidden />
                              )}
                              Query log report
                            </Button>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
                {hasMoreRuns && (
                  <div className="mt-4 flex justify-center">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={loadingMore}
                      className={brandOutlineButtonClass}
                      onClick={() => void loadMoreRuns()}
                    >
                      {loadingMore ? (
                        <LoadingSpinner size="xs" />
                      ) : (
                        'Load more runs'
                      )}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="border-t border-gray-200 px-5 py-4">
            <Button
              type="button"
              variant="outline"
              disabled={projectId == null || !infrastructureReady}
              className={cn(
                'h-11 w-full justify-center font-semibold',
                brandOutlineButtonClass,
                !infrastructureReady &&
                  'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400'
              )}
              onClick={() => setNewRunOpen(true)}
            >
              <Plus className="h-4 w-4" aria-hidden />
              New Run
            </Button>
          </div>
        </div>
      </Dialog>

      {projectId != null && infrastructureReady && (
        <CreateRunModal
          open={newRunOpen}
          onClose={() => {
            setNewRunOpen(false);
          }}
          projectId={projectId}
          projectName={projectName}
          onRunCreated={async (runId) => {
            setNewRunOpen(false);
            await refreshRuns();
            onViewRun(runId);
            onClose();
          }}
        />
      )}
    </>
  );
}
